import "element-internals-polyfill"
import { AttachInternals, Component, Element, h, Listen, Method, Prop, State, Watch } from '@stencil/core';
import '@material/web/textfield/outlined-text-field';
import '@material/web/icon/icon';

import { awaitDefinedResult, getOverriddenValidationMessage, selectFormSubmitters } from '../../utils/utils';
import { libraryComponentOptions } from '../../settings/settings';

@Component({
  tag: 'omk-text-field',
  styleUrl: 'omk-text-field.scss',
  shadow: { delegatesFocus: true },
  formAssociated: true,
})
export class OmkTextField {
  /** The current value of the text field. It is always a string. */
  @Prop({ mutable: true }) value: string = '';
  /** Label text used in the container for user input. */
  @Prop() label: string = '';
  /** Defines a string value that labels an interactive element. Used for accessibility */
  @Prop() ariaLabel: string | null;
  /** Helps the user understand what value and value format to input. */
  @Prop() placeholder: string = '';
  /** The Type attribute changes how the text field works, such as displaying a different keyboard or providing default validation. */
  @Prop() type: 'text' | 'email' | 'number' | 'password' | 'search' | 'tel' | 'url' | 'textarea' | 'date' = 'text';
  /** If true, this displays the text field as disabled and prevents interaction. */
  @Prop({ reflect: true }) disabled: boolean = false;
  /** If true, this text field is marked as required for the form it is part of. */
  @Prop({ reflect: true }) required: boolean = false;
  /** An optional prefix to display before the input value. */
  @Prop() prefixText: string = '';
  /** An optional suffix to display after the input value. */
  @Prop() suffixText: string = '';
  /** Whether or not the text field has a leading icon. Used for SSR. */
  @Prop() hasLeadingIcon: boolean = false;
  /** Whether or not the text field has a trailing icon. Used for SSR. */
  @Prop() hasTrailingIcon: boolean = false;
  /** Conveys additional information below the text field, such as how it should be used. */
  @Prop() supportingText: string = '';
  /** Override the input text CSS direction. Useful for RTL languages that use LTR notation for fractions. Valid values are 'ltr' which means left-to-right text direction and 'rtl' which means right to left text direction. */
  @Prop() textDirection: 'rtl' | 'ltr' = 'ltr';
  /** The number of rows to display for a type="textarea" text field. Defaults to 2. */
  @Prop() rows: number = 2;
  /** The inputmode is an HTML Global attribute which helps browsers or devices with on-screen keyboards to help them decide which keyboard to display when a user has selected an area for some input or textarea element. Valid values are: numeric, none, text, decimal, tel, search, email and url. */
  @Prop() inputMode: string = '';
  /** Defines the greatest value in the range of permitted values. */
  @Prop() max: string = '';
  /** The maximum number of characters a user can enter into the text field. Set to -1 for none. */
  @Prop() maxLength: number = -1;
  /** Defines the most negative value in the range of permitted values. */
  @Prop() min: string = '';
  /** The minimum number of characters a user can enter into the text field. Set to -1 for none. */
  @Prop() minLength: number = -1;
  /** A regular expression that the text field's value must match to pass constraint validation. */
  @Prop() pattern: string = '';
  /** Indicates whether or not a user should be able to edit the text field's value. */
  @Prop() readOnly: boolean = false;
  /** Indicates that input accepts multiple email addresses. */
  @Prop() multiple: boolean = false;
  /** Returns or sets the element's step attribute, which works with min and max to limit the increments at which a numeric or date-time value can be set. */
  @Prop() step: string = '';
  /** Describes what, if any, type of autocomplete functionality the input should provide. https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete contains a list of possible values. */
  @Prop() autocomplete: string = '';
  /** Name for the component. The name is used to reference the component in javascript or to reference form data if the component is part of a form. */
  @Prop() name: string = '';
  /** The unique id of the text field. The id is used to reference the component in javascript or to reference form data if the component is part of a form. */
  @Prop() inputId: string = '';

  /** A custom validation message that replaces supporting text when error is true. If customValidationMessage is an empty string, then the supporting text will continue to show. This error message overrides the error message displayed by reportValidity().
   * Marks the text field as invalid for form purposes. When set through the JS property, the error message will be show immediately.
   * When set through the HTML attribute, the validation message will only appear once the element is touched or an attempt is made to submit the form. */
  @Prop() customValidationMessage: string = '';
  /** References an HTML form. This can be used to include the text field in a form it is not a child element of. */
  @Prop() form: HTMLFormElement = undefined;

  /** @internal Do not set. Forms check this property before submit and when checkValidity() is called. */
  @Prop({ mutable: true }) validity: ValidityState;
  /** @internal Do not set - regard as read-only. The current validation message whether custom or built-in. */
  @Prop({ mutable: true }) validationMessage: string;
  /** Show validation messages immediately instead of waiting for user interaction or form submission. */
  @Prop() alwaysShowValidationMessage: boolean;
  /** Hides the inline validation messages. Used when error messages are to be displayed using other means other than the inline capability. */
  @Prop() hideValidationMessage: boolean;

  /** @internal. Set an overridden but not custom validation message from a wrapping component. This allows wrapping components like the date picker to have different overrides for built-in validation messages from this component. */
  @Prop() internalOverriddenValidationMessage: string;

  @State() touched: boolean = false;

  @Element() el: HTMLElement;

  @AttachInternals() internals: ElementInternals;

  private initialValue: string;

  @Watch('value')
  @Watch('disabled')
  valueUpdated() {
    if (!this.disabled) {
      this.internals.setFormValue(this.value);
    } else {
      this.internals.setFormValue(null);
    }
    this.validate();
  }

  @Watch('type')
  typeUpdated() {
    this.verifiedTextFieldType = this.validateTextFieldType(this.type);
    if (this.verifiedTextFieldType === 'number' && Number.isNaN(this.materialTextField.valueAsNumber)) {
      this.value = '';
      this.internals.setFormValue('');
    }
    if (this.verifiedTextFieldType === 'date' && !this.materialTextField.valueAsDate) {
      this.value = '';
      this.internals.setFormValue('');
    }
  }

  handleInnerChange(event: Event) {
    this.value = (event.target as HTMLInputElement).value;
    // change is never composed, so we must re-emit

    this.el.dispatchEvent(new Event('change', { bubbles: true, cancelable: false, composed: true }));
  }

  handleInnerInput(eventType: 'input' | 'beforeinput') {
    return (event: InputEvent) => {
      if (eventType === 'input') {
        this.value = (event.target as HTMLInputElement).value;
      }
      // if it's going to keep propagating on it's own, the shadow DOM retargetting will do the rest for us
      if (!event.composed) {
        // Autofill/autocomplete input events are not composed
        // Material updates value from input events so stopping propagation would break it
        // Input events have some props custom events don't, so we can't use Stencil's mechanism to emit
        // Sometimes the autofill events on iOS are custom events with no real detail of what changed, and we must recover the detail from the inner element. It does individual key-presses for each digit in the OTP.
        const inputEvent = new InputEvent(eventType, {
          bubbles: true,
          composed: true,
          cancelable: false,
          data: event.data ?? this.value?.[this.value?.length ?? 0 - 1] ?? this.value,
          dataTransfer: event.dataTransfer,
          inputType: event.inputType ?? 'insert-text',
          isComposing: event.isComposing,
          view: event.view,
          detail: event.detail,
          targetRanges: event.getTargetRanges ? event.getTargetRanges() : [],
        });
        this.el.dispatchEvent(inputEvent);
      }
    };
  }

  @Listen('blur')
  handleBlur() {
    this.touched = true;
    this.validate();
  }

  @Watch('alwaysShowValidationMessage')
  @Watch('customValidationMessage')
  showValidationChanged(newValue) {
    if (newValue) {
      this.touched = true;
    }
  }

  @Watch('form')
  updateForm(newValue: string) {
    // Material doesn't expose a form prop setter matching the attribute like it does for `name` so we have to explicitly set the attribute.
    if (newValue && this.materialTextField) {
      this.materialTextField.setAttribute('form', newValue);
    }
  }

  private verifiedTextFieldType = '';
  private validTextFieldTypes = ['text', 'email', 'number', 'password', 'search', 'tel', 'url', 'textarea', 'date'];

  get materialTextField() {
    return this.el.shadowRoot.querySelector('md-outlined-text-field');
  }

  componentWillLoad(): void {
    this.verifiedTextFieldType = this.validateTextFieldType(this.type);
    this.initialValue = this.el.getAttribute('value') || '';
    this.internals.setFormValue(this.value);
  }

  componentDidRender(): void {
    this.validate();
  }

  validateTextFieldType(input: string): string {
    return this.validTextFieldTypes.includes(input.toLowerCase()) ? input : 'text';
  }

  /** Checks the text field's native validation and returns whether or not the element is valid. If invalid, this method will dispatch the invalid event. */
  @Method()
  async checkValidity(): Promise<boolean> {
    return this.materialTextField.checkValidity();
  }

  /** Checks the text field's native validation and returns whether or not the element is valid. If invalid, this method will dispatch the invalid event. This method will display or clear an error text message equal to the text field's validationMessage, unless the invalid event is canceled. Use setCustomValidity() to customize the validationMessage. */
  @Method()
  async reportValidity(): Promise<boolean> {
    return this.materialTextField.reportValidity();
  }

  /** Sets a custom validation error message for the text field. Use this for custom error message. When the error is not an empty string, the text field is considered invalid and validity.customError will be true. */
  @Method()
  async setCustomValidity(error: string) {
    this.materialTextField.setCustomValidity(error);
  }

  /** Decrements the value of a numeric type text field by step or n step number of times. */
  @Method()
  async stepDown(stepDecrement?: number) {
    this.materialTextField.stepDown(stepDecrement);
  }

  /** Increments the value of a numeric type text field by step or n step number of times. */
  @Method()
  async stepUp(stepIncrement?: number) {
    this.materialTextField.stepUp(stepIncrement);
  }

  /** Reset the text field to its initial value. */
  @Method()
  async reset() {
    this.value = this.initialValue;
    this.materialTextField.value = this.initialValue;
    this.touched = false; // Reset the touched state
    this.validate();
  }

  render() {
    return (
      <md-outlined-text-field
        label={this.label}
        ariaLabel={this.ariaLabel ?? this.label}
        value={this.value}
        type={this.verifiedTextFieldType}
        placeholder={this.placeholder}
        disabled={this.disabled}
        required={this.required}
        prefixText={this.prefixText}
        suffixText={this.suffixText}
        hasLeadingIcon={this.hasLeadingIcon}
        hasTrailingIcon={this.hasTrailingIcon}
        supportingText={this.supportingText}
        textDirection={this.textDirection}
        rows={this.rows}
        inputMode={this.inputMode}
        max={this.max}
        maxLength={this.maxLength}
        min={this.min}
        minLength={this.minLength}
        pattern={this.pattern}
        readOnly={this.readOnly}
        multiple={this.multiple}
        step={this.step}
        autocomplete={this.autocomplete}
        name={this.name}
        error={(this.alwaysShowValidationMessage || this.touched) && this.validationMessage}
        errorText={this.hideValidationMessage ? '' : this.validationMessage}
        form={this.form}
      >
        <slot slot="leading-icon" name="leading-icon"></slot>
        <slot slot="trailing-icon" name="trailing-icon"></slot>
      </md-outlined-text-field>
    );
  }

  componentDidLoad() {
    const leadingIconSlotted = this.materialTextField
      .querySelector<HTMLSlotElement>('slot[name=leading-icon]')
      ?.assignedElements();
    const trailingIconSlotted = this.materialTextField
      .querySelector<HTMLSlotElement>('slot[name=trailing-icon]')
      ?.assignedElements();

    setTimeout(() => {
      this.materialTextField.hasLeadingIcon = leadingIconSlotted?.length > 0 || this.hasLeadingIcon;
      this.materialTextField.hasTrailingIcon = trailingIconSlotted?.length > 0 || this.hasTrailingIcon;
    });

    if (this.alwaysShowValidationMessage) {
      this.touched = true;
    }

    awaitDefinedResult(() =>
      this.materialTextField?.shadowRoot?.querySelector('md-outlined-field')?.querySelector('.input'),
    )
      .then((innerInput: HTMLInputElement) => {
        innerInput.addEventListener('input', this.handleInnerInput('input').bind(this));
        innerInput.addEventListener('change', this.handleInnerChange.bind(this));
        innerInput.addEventListener('beforeinput', this.handleInnerInput('beforeinput').bind(this));
      }).then(() => {
        this.materialTextField.shadowRoot.querySelector<HTMLDivElement>('div.input-wrapper').style.minHeight = 'var(--_omk-text-field-input-text-line-height)';
      })
      .catch(error => console.error(`Couldn't find inner input to attach event listeners`, error));
  }

  /** Run validation for the text field with a boolean parameter to set it as touched.
   * If `setTouched` is not provided or set to false, the touched state will not be changed.
   * Validation messages are not shown until the element is touched.
   * Custom validation messages can be set through the customValidationMessage property, and don't require this method to be called afterwards.
   * Returns true if valid or disabled, false if invalid. */
  @Method()
  async validate(setTouched?: boolean) {
    if (setTouched) {
      this.touched = true;
    }
    if (this.disabled) return true;
    if (this.customValidationMessage) {
      this.internals.setValidity(
        { customError: true },
        this.customValidationMessage,
        this.materialTextField?.shadowRoot?.getElementById('input') ?? undefined,
      );
      await this.setCustomValidity(this.customValidationMessage);
    } else {
      await this.setCustomValidity('');
      this.internals.setValidity(
        this.materialTextField.validity,
        getOverriddenValidationMessage(
          libraryComponentOptions.componentValidationMessageOverrides['omk-text-field'],
          this.materialTextField.validity,
          this.materialTextField.validationMessage,
          { step: this.step, min: this.min, max: this.max, value: this.value, valueLength: this.value?.length },
          { type: this.verifiedTextFieldType },
        ),
        this.materialTextField?.shadowRoot?.getElementById('input') ?? undefined,
      );
    }
    this.validity = this.internals?.validity;

    // force a re-render by setting a new reference
    this.validationMessage = this.internalOverriddenValidationMessage || this.internals?.validationMessage;
    return this.validity?.valid;
  }

  formAssociatedCallback(form) {
    selectFormSubmitters(form).forEach(submitter =>
      submitter.addEventListener('click', () => {
        this.touched = true;
      }),
    );
  }
}
